import { useState, useEffect, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Inbox,
  CalendarCheck,
  CheckCircle2,
  AlertCircle,
} from 'lucide-react'
import { directorApi } from '@/services/api/director'
import { TestRollbackButton } from '@/components/email/TestRollbackButton'
import { teacherPriorityApi } from '@/services/api/teacherPriority'
import type {
  DirectorPendingItem,
  DirectorHistoryItem,
  DirectorConfig,
  DirectorWorkflowItem,
  WorkflowColumn,
  DirectorDashboardSummary,
} from '@/types/director'
import {
  WORKFLOW_COLUMN_LABELS,
  TIME_IMPACT_LABELS,
} from '@/types/director'
import { extractErrorMessage } from '@/utils/errorHandler'
import { deriveWorkflowColumn } from '@/utils/directorWorkflow'
import { useToast } from '@/components/ui/ToastProvider'
import { MetricCard } from '@/components/director/MetricCard'
import { WorkflowColumn as DirectorWorkflowColumn } from '@/components/director/WorkflowColumn'
import { DirectorCard } from '@/components/director/DirectorCard'
import { QuickApproveModal } from '@/components/director/QuickApproveModal'
import { QuickRejectModal } from '@/components/director/QuickRejectModal'
import { DocumentPreviewModal } from '@/components/documents/DocumentPreviewModal'
import { LoadingSpinner } from '@/components/common'
import { todayLocalISO } from '@/lib/date-utils'
import { onCascadeStateChanged } from '@/utils/cascadeEvents'

const MAX_CARDS_PER_COLUMN = 3

function computeSummary(
  pending: DirectorPendingItem[],
  history: DirectorHistoryItem[],
): DirectorDashboardSummary {
  const now = new Date()
  const today = todayLocalISO(now)
  const startOfWeek = new Date(now)
  startOfWeek.setDate(now.getDate() - now.getDay() + 1)
  const weekStart = todayLocalISO(startOfWeek)

  const approvedToday = history.filter(h => {
    if (h.decision_type !== 'approved' || !h.created_at) return false
    const decisionDate = new Date(h.created_at).toLocaleDateString('de-CH', { year: 'numeric', month: '2-digit', day: '2-digit' }).split('.').reverse().join('-')
    return decisionDate === today
  }).length

  const processedWeek = history.filter(h => {
    if (!h.created_at) return false
    const decisionDate = new Date(h.created_at).toLocaleDateString('de-CH', { year: 'numeric', month: '2-digit', day: '2-digit' }).split('.').reverse().join('-')
    return decisionDate >= weekStart
  }).length

  return {
    pending_count: pending.length,
    approved_today_count: approvedToday,
    processed_this_week_count: processedWeek,
  }
}

function assignColumns(pending: DirectorPendingItem[]): DirectorWorkflowItem[] {
  // Fallakte v2 (P4.3): Ableitung in utils/directorWorkflow.ts geteilt
  return pending.map(item => ({ ...item, column: deriveWorkflowColumn(item) }))
}

export function DirectorDashboardPage() {
  const navigate = useNavigate()
  const { showToast } = useToast()

  const [pending, setPending] = useState<DirectorPendingItem[]>([])
  const [history, setHistory] = useState<DirectorHistoryItem[]>([])
  const [config, setConfig] = useState<DirectorConfig | null>(null)
  const [loading, setLoading] = useState(true)
  const [pendingLoaded, setPendingLoaded] = useState(false)
  const [historyLoaded, setHistoryLoaded] = useState(false)
  const [configLoaded, setConfigLoaded] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [approveModalItem, setApproveModalItem] = useState<DirectorWorkflowItem | null>(null)
  const [rejectModalItem, setRejectModalItem] = useState<DirectorWorkflowItem | null>(null)
  const [docsPreviewItem, setDocsPreviewItem] = useState<DirectorWorkflowItem | null>(null)
  const [requestingDocsId, setRequestingDocsId] = useState<string | null>(null)

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError(null)
    setPendingLoaded(false)
    setHistoryLoaded(false)
    setConfigLoaded(false)

    const results = await Promise.allSettled([
      directorApi.getPending(),
      directorApi.getHistory(),
      directorApi.getConfig(),
    ])

    if (results[0].status === 'fulfilled') {
      setPending(results[0].value)
      setPendingLoaded(true)
    } else {
      setError(extractErrorMessage(results[0].reason, { loadError: 'Ausstehende Anfragen konnten nicht geladen werden' }))
      setPendingLoaded(true)
    }

    if (results[1].status === 'fulfilled') {
      setHistory(results[1].value)
      setHistoryLoaded(true)
    }

    if (results[2].status === 'fulfilled') {
      setConfig(results[2].value)
      setConfigLoaded(true)
    }

    setLoading(false)
  }, [])

  useEffect(() => { fetchData() }, [fetchData])

  const refreshData = useCallback(async () => {
    try {
      const newPending = await directorApi.getPending()
      setPending(newPending)
    } catch {
      // Silently fail – partial refresh OK
    }
    try {
      const newHistory = await directorApi.getHistory()
      setHistory(newHistory)
    } catch {
      // Silently fail – partial refresh OK
    }
  }, [])

  useEffect(() => onCascadeStateChanged(() => { void refreshData() }), [refreshData])

  const summary = useMemo(
    () => computeSummary(pending, history),
    [pending, history]
  )

  const workflowItems = useMemo(() => assignColumns(pending), [pending])

  const itemsByColumn = useMemo(() => {
    const map: Record<WorkflowColumn, DirectorWorkflowItem[]> = {
      documents_pending: [],
      ready: [],
    }
    workflowItems.forEach(item => {
      map[item.column].push(item)
    })
    return map
  }, [workflowItems])

  const recentHistory = useMemo(() => history.slice(0, 5), [history])

  const handleApprove = (item: DirectorWorkflowItem) => {
    setApproveModalItem(item)
  }

  const handleReject = (item: DirectorWorkflowItem) => {
    setRejectModalItem(item)
  }

  const handleAbortSearch = async (item: DirectorWorkflowItem) => {
    try {
      await directorApi.abortSearch(item.id)
      showToast({ message: 'Suche abgebrochen', type: 'success', duration: 3000 })
      await refreshData()
    } catch (e: unknown) {
      showToast({
        message: extractErrorMessage(e, { loadError: 'Suche konnte nicht abgebrochen werden' }),
        type: 'error',
        duration: 4000,
      })
    }
  }

  const handleReadNotice = async (item: DirectorWorkflowItem) => {
    try {
      await directorApi.markNoticeRead(item.id)
      await refreshData()
    } catch (e: unknown) {
      showToast({
        message: extractErrorMessage(e, { loadError: 'Konnte nicht als gelesen markieren' }),
        type: 'error',
        duration: 4000,
      })
    }
  }

  const handleRequestDocs = async (item: DirectorWorkflowItem) => {
    if (item.kind === 'notice') return
    if (requestingDocsId) return
    setRequestingDocsId(item.id)
    try {
      if (item.kind === 'retro') {
        await directorApi.requestRetroDocuments(item.id)
      } else {
        await directorApi.requestRequestDocuments(item.id)
      }
      showToast({
        message: 'Dokumente angefordert — die Lehrperson wird per E-Mail informiert.',
        type: 'success',
        duration: 4000,
      })
      await refreshData()
    } catch (e: unknown) {
      showToast({
        message: extractErrorMessage(e, { loadError: 'Dokumenteanforderung fehlgeschlagen' }),
        type: 'error',
        duration: 4000,
      })
    } finally {
      setRequestingDocsId(null)
    }
  }

  const handlePreviewDocs = (item: DirectorWorkflowItem) => {
    setDocsPreviewItem(item)
  }

  const handleActionSuccess = async () => {
    setApproveModalItem(null)
    setRejectModalItem(null)
    await refreshData()
  }

  const isDirectorDisabled = configLoaded && config && !config.director_enabled && pending.length === 0 && history.length === 0

  if (loading && !pendingLoaded && !historyLoaded) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[60vh]">
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <TestRollbackButton />
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Direktor-Dashboard</h1>
        <p className="text-sm text-gray-500 mt-1">
          Übersicht der Vertretungsanfragen
        </p>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-4 h-4" />
          <span>{error}</span>
        </div>
      )}

      {isDirectorDisabled && (
        <div className="mb-4 p-3 bg-yellow-100 text-yellow-800 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-4 h-4" />
          <span>Das Director-Feature ist für diese Schule deaktiviert.</span>
        </div>
      )}

      {/* Kennzahlen */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <MetricCard
          label="Ausstehend"
          value={summary.pending_count}
          icon={<Inbox className="w-5 h-5" />}
          color="red"
          loading={!pendingLoaded}
        />
        <MetricCard
          label="Heute genehmigt"
          value={summary.approved_today_count}
          icon={<CheckCircle2 className="w-5 h-5" />}
          color="green"
          loading={!historyLoaded}
        />
        <MetricCard
          label="Diese Woche"
          value={summary.processed_this_week_count}
          icon={<CalendarCheck className="w-5 h-5" />}
          color="blue"
          loading={!historyLoaded}
        />
        <MetricCard
          label="Feature-Status"
          value={isDirectorDisabled ? 'Inaktiv' : 'Aktiv'}
          icon={isDirectorDisabled ? <AlertCircle className="w-5 h-5" /> : <CheckCircle2 className="w-5 h-5" />}
          color={isDirectorDisabled ? 'orange' : 'green'}
          loading={!configLoaded}
        />
      </div>

      {/* Workflow-Spalten */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <DirectorWorkflowColumn
          title={WORKFLOW_COLUMN_LABELS.documents_pending}
          column="documents_pending"
          count={itemsByColumn.documents_pending.length}
          emptyText="Keine offenen Dokumente"
        >
          {itemsByColumn.documents_pending.slice(0, MAX_CARDS_PER_COLUMN).map(item => (
            <DirectorCard
              key={item.id}
              item={item}
              onOpen={() => { if (item.kind !== 'notice' && item.kind !== 'retro') navigate(`/director/decisions/${item.id}`) }}
              onApprove={() => handleApprove(item)}
              onReject={() => handleReject(item)}
              onRequestDocs={() => handleRequestDocs(item)}
              onPreviewDocs={() => handlePreviewDocs(item)}
              onAbortSearch={() => handleAbortSearch(item)}
              onReadNotice={() => handleReadNotice(item)}
              requestingDocs={requestingDocsId === item.id}
              canRequestDocs={config?.can_request_documents !== false}
            />
          ))}
          {itemsByColumn.documents_pending.length > MAX_CARDS_PER_COLUMN && (
            <button
              onClick={() => navigate('/director/decisions')}
              className="w-full text-center text-sm text-blue-600 hover:text-blue-700 py-2"
            >
              Alle {itemsByColumn.documents_pending.length} anzeigen →
            </button>
          )}
        </DirectorWorkflowColumn>

        <DirectorWorkflowColumn
          title={WORKFLOW_COLUMN_LABELS.ready}
          column="ready"
          count={itemsByColumn.ready.length}
          emptyText="Keine entscheidungsbereiten Anfragen"
        >
          {itemsByColumn.ready.slice(0, MAX_CARDS_PER_COLUMN).map(item => (
            <DirectorCard
              key={item.id}
              item={item}
              onOpen={() => { if (item.kind !== 'notice' && item.kind !== 'retro') navigate(`/director/decisions/${item.id}`) }}
              onApprove={() => handleApprove(item)}
              onReject={() => handleReject(item)}
              onRequestDocs={() => handleRequestDocs(item)}
              onPreviewDocs={() => handlePreviewDocs(item)}
              onAbortSearch={() => handleAbortSearch(item)}
              onReadNotice={() => handleReadNotice(item)}
              requestingDocs={requestingDocsId === item.id}
              canRequestDocs={config?.can_request_documents !== false}
            />
          ))}
        </DirectorWorkflowColumn>
      </div>

      {/* Letzte Entscheidungen */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-gray-700">Letzte Entscheidungen</h2>
          <button
            onClick={() => navigate('/director/decisions')}
            className="text-xs text-blue-600 hover:text-blue-700"
          >
            Zum vollständigen Verlauf →
          </button>
        </div>
        {!historyLoaded ? (
          <div className="flex justify-center py-6"><LoadingSpinner /></div>
        ) : recentHistory.length === 0 ? (
          <div className="px-4 py-8 text-center text-gray-400 text-sm">
            Noch keine Entscheidungen
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Datum</th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Lehrer</th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Entscheidung</th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Zeit</th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Direktor</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {recentHistory.map(h => {
                  const caseId = h.substitution_request_id
                  return (
                    <tr
                      key={h.decision_id}
                      className={caseId ? 'hover:bg-gray-50 cursor-pointer' : 'hover:bg-gray-50'}
                      onClick={caseId ? () => navigate(`/director/decisions/${caseId}`) : undefined}
                      onKeyDown={caseId ? (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          navigate(`/director/decisions/${caseId}`)
                        }
                      } : undefined}
                      tabIndex={caseId ? 0 : undefined}
                      role={caseId ? 'link' : undefined}
                    >
                      <td className="px-4 py-2.5 text-sm">{h.date}</td>
                      <td className="px-4 py-2.5 text-sm">{h.requesting_teacher_name}</td>
                      <td className="px-4 py-2.5">
                        <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                          h.decision_type === 'approved' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {h.decision_type === 'approved' ? 'Genehmigt' : 'Abgelehnt'}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-sm">{TIME_IMPACT_LABELS[h.time_impact] || h.time_impact}</td>
                      <td className="px-4 py-2.5 text-sm">{h.director_name}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modals */}
      <QuickApproveModal
        isOpen={!!approveModalItem}
        item={approveModalItem}
        config={config}
        onClose={() => setApproveModalItem(null)}
        onSuccess={handleActionSuccess}
      />
      <QuickRejectModal
        isOpen={!!rejectModalItem}
        item={rejectModalItem}
        config={config}
        onClose={() => setRejectModalItem(null)}
        onSuccess={handleActionSuccess}
      />
      <DocumentPreviewModal
        isOpen={!!docsPreviewItem}
        onClose={() => setDocsPreviewItem(null)}
        fetchDocuments={() => teacherPriorityApi.getRequestDocuments(docsPreviewItem!.id)}
        fetchFile={(docId) => teacherPriorityApi.downloadRequestDocument(docsPreviewItem!.id, docId)}
      />
    </div>
  )
}
